import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Building2 } from 'lucide-react';
import { useOrganizations } from '../../hooks/useOrganizations';
import { useCurrentOrganization } from '../../contexts/OrganizationContext';
import { OrganizationCard } from '../../components/organizations/OrganizationCard';
import { CreateOrganizationModal } from '../../components/organizations/CreateOrganizationModal';
import { Button, EmptyState } from '../../ui';
import './OrganizationListPage.css';

export function OrganizationListPage() {
  const navigate = useNavigate();
  const { data: organizations, isLoading, error } = useOrganizations();
  const { setCurrentOrgId } = useCurrentOrganization();
  const [showCreateModal, setShowCreateModal] = useState(false);

  const handleSelectOrg = (orgId: string) => {
    setCurrentOrgId(orgId);
    navigate(`/organizations/${orgId}`);
  };

  const handleCreateSuccess = (orgId: string) => {
    setShowCreateModal(false);
    setCurrentOrgId(orgId);
    navigate(`/organizations/${orgId}`);
  };

  if (isLoading) {
    return (
      <div className="org-list-page">
        <div className="org-list-page__loading">
          <div className="spinner" />
          <p>Loading organizations...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="org-list-page">
        <div className="org-list-page__error">
          <p>Failed to load organizations</p>
          <p className="org-list-page__error-detail">{error.message}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="org-list-page">
      <header className="org-list-page__header">
        <div>
          <h1>Your Organizations</h1>
          <p className="org-list-page__subtitle">Every workspace you belong to lives here.</p>
        </div>
        <Button onClick={() => setShowCreateModal(true)}>+ Create Organization</Button>
      </header>

      {organizations && organizations.length > 0 ? (
        <div className="org-list-page__grid">
          {organizations.map(org => (
            <OrganizationCard
              key={org.id}
              organization={org}
              onClick={() => handleSelectOrg(org.id)}
            />
          ))}
        </div>
      ) : (
        <EmptyState
          icon={<Building2 size={20} />}
          title="No organizations yet"
          description="Create your first organization to get started."
          action={<Button onClick={() => setShowCreateModal(true)}>Create Organization</Button>}
        />
      )}

      {showCreateModal && (
        <CreateOrganizationModal
          onClose={() => setShowCreateModal(false)}
          onSuccess={handleCreateSuccess}
        />
      )}
    </div>
  );
}
